import React, { useRef, useEffect, useState } from "react";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const MURA_TYPE_DATA = {
  pressing_light_leakage: { id: '0', color: '#FF4500' },
  dark_state_mura: { id: '1', color: '#4682B4' },
  clouding: { id: '2', color: '#9932CC' },
  wavy_mura: { id: '3', color: '#FFD700' },
  lamp_shades: { id: '4', color: '#32CD32' },
  light_bract: { id: '5', color: '#FF6347' },
  grid_mura: { id: '6', color: '#708090' },
  corner_shadow: { id: '7', color: '#FFA500' },
  bright_side_around: { id: '8', color: '#000080' },
  dark_spot: { id: '9', color: '#A52A2A' },
  bright_spot: { id: '10', color: '#9370DB' },
  horizontal_band: { id: '11', color: '#3CB371' },
  vertical_band: { id: '12', color: '#00BFFF' },
  horizontal_line: { id: '13', color: '#FF69B4' },
  vertical_line: { id: '14', color: '#A9A9A9' },
  other: { id: '15', color: '#DC143C' },
};

const PhotoCard = ({ title, buttonLabel, inputType, labelText, imageUrl, defects }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [loading, setLoading] = useState(title === "Original Image" || title === "Labeled Image");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  const handleImageClick = () => {
    if (!imageUrl && title !== "Labeled Image") return;

    if (title === "Labeled Image" && canvasRef.current) {
      const canvas = canvasRef.current;
      setPreviewImageUrl(canvas.toDataURL("image/png"));
    } else {
      setPreviewImageUrl(imageUrl);
    }
    setIsPreviewOpen(true);
  };

  const handleDownload = async () => {
    if (!imageUrl) {
      console.error("No image URL provided for download");
      alert("No image available for download.");
      return;
    }
    if (title === "Original Image" && imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      try {
        setLoading(true);
        const canvas = document.createElement("canvas");
        canvas.width = imgRef.current.naturalWidth;
        canvas.height = imgRef.current.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgRef.current, 0, 0);
        const url = canvas.toDataURL("image/jpeg", 0.9); 
        const link = document.createElement("a");
        link.href = url;
        const baseFilename = imageUrl.split("/").pop()?.split(".")[0] || "image";
        link.setAttribute("download", `${baseFilename}.jpg`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } catch (error) {
        console.error("Canvas download failed:", error);
        alert("Canvas-based download failed. Attempting server download...");
      } finally {
        setLoading(false);
      }
    }
    try {
      setLoading(true);
      const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Proxy response error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText.slice(0, 500),
        });
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const contentType = response.headers.get("Content-Type");
      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        const text = await blob.text();
        console.error("Invalid content received:", {
          contentType: blob.type,
          body: text.slice(0, 500),
        });
        throw new Error(`Downloaded content is not a valid image. Content-Type: ${blob.type}`);
      }

      let extension = "jpg";
      if (contentType) {
        if (contentType.includes("image/png")) {
          extension = "png";
        } else if (contentType.includes("image/jpeg") || contentType.includes("image/jpg")) {
          extension = "jpg";
        } else if (contentType.includes("image/gif")) {
          extension = "gif";
        } else if (contentType.includes("image/webp")) {
          extension = "webp";
        } else {
          console.warn(`Unsupported Content-Type: ${contentType}. Defaulting to .jpg`);
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const baseFilename = imageUrl.split("/").pop()?.split(".")[0] || "image";
      const filename = `${baseFilename}.${extension}`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Failed to download the image: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!imageUrl) return;

    if (title === "Labeled Image" && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Failed to get 2D context for canvas");
        return;
      }

      const img = new Image();
      img.crossOrigin = "Anonymous";
      const urlWithCacheBust = `${imageUrl}?ts=${new Date().getTime()}`;

      img.onerror = () => {
        console.warn("CORS failed for canvas image");
        img.src = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      };

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.maxWidth = '100%';
        canvas.style.maxHeight = '100%';
        canvas.style.objectFit = 'contain';

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

        if (defects?.length) {
          defects.forEach((defect, index) => {
            if (!defect.bbox || !Array.isArray(defect.bbox) || defect.bbox.length !== 4) {
              console.warn(`Defect ${index} has invalid bbox:`, defect.bbox);
              return;
            }

            let [center_x, center_y, width, height] = defect.bbox;

            if (
              !Number.isFinite(center_x) ||
              !Number.isFinite(center_y) ||
              !Number.isFinite(width) ||
              !Number.isFinite(height)
            ) {
              console.warn(`Defect ${index} has invalid bbox values:`, defect.bbox);
              return;
            }

            center_x = Math.max(0, Math.min(1, center_x));
            center_y = Math.max(0, Math.min(1, center_y));
            width = Math.max(0, Math.min(1, width));
            height = Math.max(0, Math.min(1, height));

            const x = (center_x - width / 2) * img.naturalWidth;
            const y = (center_y - height / 2) * img.naturalHeight;
            const pixelWidth = width * img.naturalWidth;
            const pixelHeight = height * img.naturalHeight;

            const edgeThreshold = 10;
            const clampedX = Math.max(0, Math.min(x, img.naturalWidth - pixelWidth));
            const clampedY = Math.max(0, Math.min(y, img.naturalHeight - pixelHeight));
            const clampedWidth = Math.min(pixelWidth, img.naturalWidth - clampedX);
            const clampedHeight = Math.min(pixelHeight, img.naturalHeight - clampedY);

            const finalX = Math.max(edgeThreshold, Math.min(clampedX, img.naturalWidth - clampedWidth - edgeThreshold));
            const finalY = Math.max(edgeThreshold, Math.min(clampedY, img.naturalHeight - clampedHeight - edgeThreshold));
            const finalWidth = Math.min(clampedWidth, img.naturalWidth - finalX - edgeThreshold);
            const finalHeight = Math.min(clampedHeight, img.naturalHeight - finalY - edgeThreshold);

            const color = '#FF0000'; 

            ctx.strokeStyle = color;
            ctx.lineWidth = 4;
            ctx.strokeRect(finalX, finalY, finalWidth, finalHeight);

            if (defect.muraType && defect.muraType.toLowerCase() !== 'other') {
              ctx.fillStyle = color;
              ctx.font = 'bold 20px Arial';
              const label = defect.muraLabId ? `ID: ${defect.muraLabId} | ${defect.muraType}` : defect.muraType;
              const textMetrics = ctx.measureText(label);
              const textWidth = textMetrics.width;
              const textHeight = 16;
              const topThreshold = 20;
              const rightThreshold = 20;

              const labelY = finalY < topThreshold ? finalY + finalHeight + textHeight + 20 : finalY - 20;
              let labelX = finalX;
              if (labelX + textWidth > img.naturalWidth - rightThreshold) {
                labelX = Math.max(edgeThreshold, finalX + finalWidth - textWidth - 10);
              }

              ctx.fillText(label, labelX, labelY);
            }
          });
        }
        setLoading(false);
      };

      img.src = urlWithCacheBust;
    }

    if (title === "Original Image" && imgRef.current) {
      const img = imgRef.current;
      const urlWithCacheBust = `${imageUrl}?ts=${new Date().getTime()}`;

      img.crossOrigin = "Anonymous";
      img.onerror = () => {
        console.warn("CORS failed for original image");
        img.src = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      };
      img.onload = () => setLoading(false);
      img.src = urlWithCacheBust;
    }
  }, [imageUrl, defects, title]);

  const getDefectInfo = () => {
    if (!defects?.length) return "No defect information available";
    return defects
      .filter(defect => defect.muraType.toLowerCase() !== 'other')
      .map((defect) => {
        const infoParts = [];
        infoParts.push(` ${defect.muraType || "Unknown"}`);
        if (defect.muraLabelIssue?.muraRootCause) {
          infoParts.push(`${defect.muraLabelIssue.muraRootCause}`);
        }
        if (defect.muraLabelIssue?.muraRootIssue?.length) {
          infoParts.push(`${defect.muraLabelIssue.muraRootIssue.join(", ")}`);
        }
        if (defect.confidenceScore) {
          infoParts.push(`Confidence: ${Math.round(defect.confidenceScore * 100)}%`);
        }
        return infoParts.join(" : ");
      })
      .join("\n");
  };

  return (
    <div className="mb-6 w-full max-w-full">
      <h3 className="text-base sm:text-lg font-medium mb-2 w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl mx-auto">{title}</h3>

      <div
        className={`
          relative ${loading ? "bg-gray-800" : !imageUrl && title !== "Labeled Image" ? "bg-gray-800" : "bg-transparent"} overflow-hidden 
          flex items-center justify-center
          w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl mx-auto
          h-[180px] sm:h-[300px] md:h-[400px] lg:h-[500px]
          cursor-pointer p-0 m-0
        `}
        onClick={imageUrl || title === "Labeled Image" ? handleImageClick : null}
      >
        {loading && (
          <LoaderCircle className="animate-spin text-white w-8 h-8 absolute z-10" />
        )}

        {title === "Labeled Image" ? (
          <canvas
            ref={canvasRef}
            className={`w-full h-full object-contain border border-gray-600 ${
              loading ? "opacity-0" : "opacity-100"
            }`}
          />
        ) : title === "Original Image" ? (
          <img
            ref={imgRef}
            alt="Original"
            className={`w-full h-full object-contain border border-gray-600 ${
              loading ? "opacity-0" : "opacity-100"
            }`}
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-contain border border-gray-600"
            crossOrigin="anonymous"
            onError={(e) => {
              e.target.src = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
            }}
          />
        ) : (
          <span className="text-white text-xl">No Re-labeled Image</span>
        )}
      </div>

      {buttonLabel && (
        <div className="w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl mx-auto flex justify-end">
          <button
            className="mt-3 bg-blue-500 text-white px-4 sm:px-5 py-1 sm:py-2 rounded-full text-sm"
            onClick={handleDownload}
            disabled={loading || !imageUrl}
          >
            {loading ? "Download" : buttonLabel}
          </button>
        </div>
      )}
      {inputType === "input" && (
        <div className="w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl mx-auto">
          {labelText && (
            <p className="mt-4 text-base sm:text-lg font-medium mb-2">{labelText}</p>
          )}
          {!loading && (
            <textarea
              readOnly
              value={getDefectInfo()}
              className="w-full h-24 sm:h-32 px-3 py-2 rounded bg-gray-800 text-white border border-gray-600 text-sm sm:text-base"
              rows={6}
            />
          )}
        </div>
      )}

      {isPreviewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative bg-gray-900 p-4 rounded-lg w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl max-h-[90vh]">
            <button
              className="absolute top-2 right-2 text-white text-xl sm:text-2xl"
              onClick={() => setIsPreviewOpen(false)}
            >
              &times;
            </button>
            <h3 className="text-base sm:text-lg font-medium text-white mb-4">{title}</h3>
            <img
              src={previewImageUrl}
              alt={title}
              className="w-full h-auto max-h-[70vh] sm:max-h-[75vh] md:max-w-3xl lg:max-w-4xl object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const PhotoReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cardId,
    defectType = "Unknown",
    filters = {},
    chartData = [],
    sizeOptions = ["All"],
    odmOptions = ["All"],
    brandOptions = ["All"],
    images = [],
    imageCount = 0,
    analysis = {},
  } = location.state || {};

  const selectedImage = images.find((item) => item._id === cardId);
  const defects = selectedImage?.MuraRecording?.muraLabeling?.defects || [];
  const rawImageUrl = selectedImage?.MuraRecording?.rawImageUrl || analysis?.rawImageUrl;

  const handleBackClick = () => {
    navigate("/photo-gallery", {
      state: {
        defectType,
        filters,
        chartData,
        sizeOptions,
        odmOptions,
        brandOptions,
        images,
        imageCount,
        analysis,
      },
    });
  };

  return (
    <div className="bg-black text-white min-h-screen p-3 sm:p-6 font-sans">
      <header className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-10 w-full max-w-[80vw] sm:max-w-[70vw] md:max-w-3xl lg:max-w-4xl mx-auto">
        <ArrowLeft
          className="w-6 h-6 sm:w-8 sm:h-8 cursor-pointer"
          onClick={handleBackClick}
        />
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center sm:text-left w-full">
          Photo Review
        </h2>
      </header>

      <div className="flex flex-col items-center gap-6 sm:gap-8">
        <PhotoCard
          title="Original Image"
          buttonLabel="Download"
          imageUrl={rawImageUrl}
        />
        <PhotoCard
          title="Labeled Image"
          inputType="input"
          labelText="Labeling Recording"
          imageUrl={rawImageUrl}
          defects={defects}
        />
      </div>
    </div>
  );
};

export default PhotoReview;